import React, { useState } from "react";

// Conversion factors
const distanceUnits = [
  { label: "Inches", value: "in", toMeters: v => v * 0.0254 },
  { label: "Feet", value: "ft", toMeters: v => v * 0.3048 },
  { label: "Miles", value: "mi", toMeters: v => v * 1609.34 },
  { label: "Centimeters", value: "cm", toMeters: v => v * 0.01 },
  { label: "Meters", value: "m", toMeters: v => v },
  { label: "Kilometers", value: "km", toMeters: v => v * 1000 }
];

const timeUnits = [
  { label: "Seconds", value: "s", toSeconds: v => v },
  { label: "Minutes", value: "min", toSeconds: v => v * 60 },
  { label: "Hours", value: "h", toSeconds: v => v * 3600 }
];

export default function AGVLoopSizingTool() {
  // User settings
  const [deliveriesPerHour, setDeliveriesPerHour] = useState(60);

  // Unit selection
  const [distanceUnit, setDistanceUnit] = useState("m");
  const [timeUnit, setTimeUnit] = useState("s");

  // Inputs
  const [loopDistance, setLoopDistance] = useState(200); // AGV completes this loop each cycle
  const [speed, setSpeed] = useState(1);
  const [loadTime, setLoadTime] = useState(30);
  const [unloadTime, setUnloadTime] = useState(30);

  // Efficiency Breakdown
  const [congestionEff, setCongestionEff] = useState(85); // %
  const [runBeforeCharge, setRunBeforeCharge] = useState(60); // time unit
  const [chargeTime, setChargeTime] = useState(10); // time unit

  // Find conversion functions
  const distConv = distanceUnits.find(u => u.value === distanceUnit);
  const timeConv = timeUnits.find(u => u.value === timeUnit);

  // Convert all user inputs to meters/seconds for calculations
  const loopDistance_m = distConv.toMeters(Number(loopDistance));
  // Speed: convert to m/s
  const speed_mps = distConv.toMeters(Number(speed)) / timeConv.toSeconds(1);

  // Times: convert to seconds
  const loadTime_s = timeConv.toSeconds(Number(loadTime));
  const unloadTime_s = timeConv.toSeconds(Number(unloadTime));
  const runBeforeCharge_s = timeConv.toSeconds(Number(runBeforeCharge));
  const chargeTime_s = timeConv.toSeconds(Number(chargeTime));

  // Battery utilization ratio
  const utilization = (runBeforeCharge_s) / (runBeforeCharge_s + chargeTime_s);

  // Overall AGV efficiency (as fraction)
  const overallEfficiency = (congestionEff / 100) * utilization;

  // --- AGV Sizing Calculations (in SI units) ---
  const cycleTime = (loopDistance_m / speed_mps) + loadTime_s + unloadTime_s;
  const effectiveCycleTime = cycleTime / overallEfficiency;

  // Theoretical max trips per AGV per hour (could be fractional AGVs)
  const tripsPerHourTheoretical = 3600 / effectiveCycleTime;

  // AGVs needed (rounded up)
  const agvsNeeded = Math.ceil(deliveriesPerHour / tripsPerHourTheoretical);

  // Actual trips per AGV per hour, given the rounded-up fleet size
  const actualTripsPerHour = deliveriesPerHour / agvsNeeded;

  // AGV departure interval (seconds and minutes)
  const agvDepartureInterval = effectiveCycleTime / agvsNeeded; // seconds between departures
  const agvDepartureIntervalMinutes = agvDepartureInterval / 60;

  // Speed label (dynamic)
  const speedLabel = `${distConv.label.toLowerCase()} per ${timeConv.label.toLowerCase()}`;

  return (
    <div style={{
      maxWidth: 540, margin: "auto", padding: 24,
      border: "1px solid #ccc", borderRadius: 12, fontFamily: "sans-serif"
    }}>
      <h2>AGV Fleet Sizing Tool</h2>
      <div style={{
        padding: "12px 14px", background: "#f4faff", borderRadius: 8, fontSize: 15, marginBottom: 16,
        border: "1px solid #e6f2fc"
      }}>
        <strong>Purpose:</strong> This calculator estimates the required number of AGVs for a <b>simple delivery loop</b> that cycles between a single, fixed pickup location and a single, fixed delivery location. <br />
        It assumes all AGVs follow the same closed loop and that loading and unloading times occur at just these two locations. <br />
        This tool is <u>not</u> intended for systems with multiple delivery or pickup points, complex routings, or dynamic dispatching.
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 18 }}>
        <div>
          <label>Distance Unit<br />
            <select value={distanceUnit} onChange={e => setDistanceUnit(e.target.value)}>
              {distanceUnits.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </label>
        </div>
        <div>
          <label>Time Unit<br />
            <select value={timeUnit} onChange={e => setTimeUnit(e.target.value)}>
              {timeUnits.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <div>
        <label>
          Deliveries/hour:{" "}
          <input type="number" value={deliveriesPerHour}
            min={1} onChange={e => setDeliveriesPerHour(e.target.value)} />
        </label>
      </div>
      <div>
        <label>
          AGV Loop Distance ({distConv.label}):{" "}
          <input type="number" value={loopDistance}
            min={0.01} step={0.01} onChange={e => setLoopDistance(e.target.value)} />
        </label>
      </div>
      <div>
        <label>
          AGV Speed ({speedLabel}):{" "}
          <input type="number" value={speed}
            min={0.01} step={0.01} onChange={e => setSpeed(e.target.value)} />
        </label>
      </div>
      <div>
        <label>
          Loading Time ({timeConv.label}):{" "}
          <input type="number" value={loadTime}
            min={0} onChange={e => setLoadTime(e.target.value)} />
        </label>
      </div>
      <div>
        <label>
          Unloading Time ({timeConv.label}):{" "}
          <input type="number" value={unloadTime}
            min={0} onChange={e => setUnloadTime(e.target.value)} />
        </label>
      </div>
      <hr />
      <h4>AGV Availability & Efficiency Factors</h4>
      <div>
        <label>
          Congestion/Delays Efficiency (% productive time):{" "}
          <input type="number" value={congestionEff}
            min={50} max={100} step={1}
            onChange={e => setCongestionEff(e.target.value)} />%
        </label>
      </div>
      <div>
        <label>
          Run Before Charge ({timeConv.label}):{" "}
          <input type="number" value={runBeforeCharge}
            min={1} step={1}
            onChange={e => setRunBeforeCharge(e.target.value)} />
        </label>
      </div>
      <div>
        <label>
          Charge Time Needed ({timeConv.label}):{" "}
          <input type="number" value={chargeTime}
            min={0} step={1}
            onChange={e => setChargeTime(e.target.value)} />
        </label>
      </div>
      <div style={{ fontSize: 13, color: "#555", margin: "8px 0" }}>
        <em>AGV can run for {runBeforeCharge} {timeConv.label}, then must charge for {chargeTime} {timeConv.label}.</em>
        <br />
        Battery Utilization: {(utilization * 100).toFixed(1)}%<br />
        Overall AGV Availability: {(overallEfficiency * 100).toFixed(1)}%
      </div>
      <hr />
      <h3>Result</h3>
      <div>
        <strong>AGVs Needed: {isNaN(agvsNeeded) ? "–" : agvsNeeded}</strong>
      </div>
      <div>
        <strong>AGV Departure Interval:</strong>{" "}
        {isNaN(agvDepartureInterval) ? "–" : agvDepartureInterval.toFixed(1)} seconds
        {" "}({isNaN(agvDepartureIntervalMinutes) ? "–" : agvDepartureIntervalMinutes.toFixed(2)} min)
      </div>
      <div style={{ fontSize: 12, color: "#555" }}>
        This is the average time between AGVs leaving the pickup location to start a delivery run.
      </div>
      <div style={{ fontSize: 12, color: "#555", marginTop: 8 }}>
        <div>
          Theoretical max: {tripsPerHourTheoretical > 0 ? tripsPerHourTheoretical.toFixed(2) : "–"} trips/hour per AGV
        </div>
        <div>
          Actual in system: {actualTripsPerHour > 0 ? actualTripsPerHour.toFixed(2) : "–"} trips/hour per AGV
        </div>
      </div>
      <hr />
      <div style={{
        fontSize: 13,
        background: "#fffbe6",
        color: "#b8860b",
        border: "1px solid #ffe58f",
        borderRadius: 8,
        padding: 12,
        marginTop: 24
      }}>
        <strong>Disclaimer:</strong> The results provided by this AGV Fleet Sizing Tool are for informational and entertainment purposes only. This tool makes basic simplifying assumptions and <b>should not be used for engineering, procurement, or operational decisions</b>. Always consult with qualified professionals for actual system sizing and design.
      </div>
    </div>
  );
}
